import { type MouseEvent, type ReactNode } from 'react';
import { Link } from '@carbon/react';
import type { SearchParams } from 'atoms/search-atom';
import type { DataTableColumnDef } from 'components/data-table';
import type { DivisionOrderResult } from 'pages/division-orders/list/types';
import type { RoyaltyCheckResult } from 'pages/royalty-checks/list/types';
import type { DocumentResponse, WellsResponse } from 'types/api-responses';
import {
  AFE_TYPE_LABELS,
  WELL_TYPE_LABELS,
  type AfeCatalogResult,
} from 'pages/afe-catalog/types';
import {
  DOCUMENT_NATIVE_KINDS,
  EMPTY,
  type RelatedItem,
  type RelatedItemKind,
} from './types';

// Single source of truth for the six record-link kinds: picker columns, search resource,
// download hint, and accordion labels. The snapshot is built server-side (RelatedItem::Source).

export type PickerRow = Record<string, unknown>;

// SearchParams plus the flat per-resource filters the pickers send.
export interface PickerSearchParams extends SearchParams {
  documentType?: string;
  tractId?: string;
  titleSupportWorkspaceId?: number;
}

// One accordion row: the `details` key the backend snapshot writes and the label
// it renders under.
export interface ReaderField {
  key: string;
  label: string;
  // Identity field — href derived from the row's target/kind (see recordDetailHref).
  recordLink?: boolean;
  // Cross-reference field — href read from details[hrefKey].
  hrefKey?: string;
}

export interface RecordKindConfig {
  href: string;
  // The polymorphic base_class Rails stores in `target_type`. Never an STI
  // subclass — `kind` is what tells the document types apart.
  targetType: string;
  searchPlaceholder: string;
  // Static filter params merged into every search (e.g. the documents subtype).
  extraParams?: Partial<PickerSearchParams>;
  columns: DataTableColumnDef<PickerRow>[];
  getRowId: (row: PickerRow) => string;
  // Whether a Download action should show; the server resolves the URL live.
  downloadable?: (row: PickerRow) => boolean;
  detailFields: ReaderField[];
}

// ── Value helpers ─────────────────────────────────────────────────────────────

const str = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const s = String(value).trim();
  return s === '' ? undefined : s;
};

const dash = (value: unknown): string => str(value) ?? EMPTY;

const fmtUsd = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return str(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n);
};

const rcSourceLabel = (source: string | null | undefined) => {
  return source === 'Provisioned' ? 'Auto-Connected' : str(source);
};

const distinctPropertyCount = (
  lineItems: { propertyName: string | null }[] | undefined
): number | undefined => {
  if (!lineItems?.length) return undefined;
  const names = new Set(
    lineItems.map((li) => li.propertyName).filter((n): n is string => !!n)
  );
  return names.size || undefined;
};

// Grantor / grantee names joined into one "A, B" string (Conveyor / Conveyee).
const partyNames = (
  parties: { name?: string | null }[] | undefined
): string | undefined => {
  const names = (parties ?? [])
    .map((party) => str(party.name))
    .filter((name): name is string => !!name);
  return names.length ? names.join(', ') : undefined;
};

// A picker column. Every column def carries an explicit `id` — DataTable sorts
// id-less columns to the end regardless of declaration order.
const column = (
  id: string,
  header: string,
  render: (row: PickerRow) => ReactNode
): DataTableColumnDef<PickerRow> => ({
  id,
  header,
  cell: ({ row }) => render(row.original),
});

// Detail-page path per record kind. Wells resolve by UWI (their show route is keyed by
// uwi); every other kind resolves by its target/primary id.
const RECORD_DETAIL_PATH: Partial<
  Record<RelatedItemKind, (id: string) => string>
> = {
  afe: (id) => `/afe_catalog/${id}`,
  division_order: (id) => `/division_orders/${id}`,
  royalty_check: (id) => `/royalty_checks/${id}`,
  courthouse_document: (id) => `/courthouse_documents/${id}`,
  regulatory_document: (id) => `/regulatory_documents/${id}`,
  well: (id) => `/wells/${id}`,
};

// A panel-uploaded afe/DO/RC row targets its source Document, which has no catalog page
// until auto-connect resolves it — so it links to that document's own viewer instead.
// Courthouse/regulatory target a Document normally and resolve via RECORD_DETAIL_PATH.
const DOCUMENT_VIEWER_PATH: Partial<
  Record<RelatedItemKind, (id: string) => string>
> = {
  afe: (id) => `/afe_documents/${id}`,
  division_order: (id) => `/division_order_documents/${id}`,
  royalty_check: (id) => `/royalty_check_documents/${id}`,
};

// A provisioned afe/DO/RC upload still targeting its raw Document — not yet auto-connected
// to a catalog record. Such a row links to the document viewer and shows no expand section.
export const awaitsCatalogRecord = (doc: RelatedItem): boolean =>
  doc.target?.type === 'Document' && !DOCUMENT_NATIVE_KINDS.includes(doc.kind);

// Identity link to a related item's page. Wells use the snapshot UWI; a provisioned
// afe/DO/RC row links to its source document's viewer; every other kind uses target.id.
export const recordDetailHref = (doc: RelatedItem): string | null => {
  if (doc.kind === 'well') {
    const uwi = doc.details?.uwi as string | undefined;
    return uwi ? RECORD_DETAIL_PATH.well!(uwi) : null;
  }
  const build = awaitsCatalogRecord(doc)
    ? DOCUMENT_VIEWER_PATH[doc.kind]
    : RECORD_DETAIL_PATH[doc.kind];
  return build && doc.target?.id ? build(String(doc.target.id)) : null;
};

export const entityPath = (
  id: number | string | null | undefined
): string | null => {
  if (id === null || id === undefined || String(id) === '') return null;
  return `/entities/${id}`;
};

// A cell value linked to a route, opened in a new tab so the picker/panel survives the
// click; stopPropagation keeps the click from toggling row selection. Plain text when
// there's no href or nothing to show.
const linkCell = (
  value: string,
  href: string | null | undefined
): ReactNode => {
  if (!href || value === EMPTY) return value;
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e: MouseEvent) => e.stopPropagation()}
    >
      {value}
    </Link>
  );
};

// ── Per-record-kind configuration ───────────────────────────────────────────

const afe = (row: PickerRow) => row as unknown as AfeCatalogResult;

const afeConfig: RecordKindConfig = {
  href: '/queries/afe_catalog',
  targetType: 'AfeHeader',
  searchPlaceholder: 'Search AFE records…',
  getRowId: (row) => String(afe(row).id),
  columns: [
    column('afeId', 'AFE ID', (row) => {
      const id = afe(row).id;
      return linkCell(
        dash(afe(row).afeNumber ?? id),
        id !== null && id !== undefined ? `/afe_catalog/${id}` : null
      );
    }),
    column('wellName', 'Well Name', (row) =>
      linkCell(dash(afe(row).well?.name), afe(row).well?.href)
    ),
    column('senderEntity', 'Sender Entity', (row) => {
      const e = afe(row).entities?.find((x) => x.role === 'sender');
      return linkCell(dash(e?.name), entityPath(e?.id));
    }),
    column('receiverEntity', 'Receiver Entity', (row) => {
      const e = afe(row).entities?.find((x) => x.role === 'receiver');
      return linkCell(dash(e?.name), entityPath(e?.id));
    }),
    column('wellType', 'Well Type', (row) => {
      const t = afe(row).well?.type;
      return dash(t ? (WELL_TYPE_LABELS[t] ?? t) : undefined);
    }),
    column('afeType', 'AFE Type', (row) => {
      const t = afe(row).afeType;
      return dash(t ? (AFE_TYPE_LABELS[t] ?? t) : undefined);
    }),
    column('operator', 'Operator', (row) => dash(afe(row).operator)),
    column('county', 'County', (row) => dash(afe(row).countyName)),
    column('state', 'State', (row) => dash(afe(row).state)),
  ],
  downloadable: (row) =>
    !!(
      afe(row).supportingFiles?.[0]?.downloadUrl ??
      afe(row).sourceDocument?.fileUrl
    ),
  detailFields: [
    { key: 'afeId', label: 'AFE ID', recordLink: true },
    { key: 'afeNumber', label: 'AFE Number' },
    {
      key: 'senderEntity',
      label: 'Sender Entity',
      hrefKey: 'senderEntityHref',
    },
    { key: 'operator', label: 'Operator', hrefKey: 'operatorHref' },
    {
      key: 'receiverEntity',
      label: 'Receiver Entity',
      hrefKey: 'receiverEntityHref',
    },
    { key: 'afeDate', label: 'AFE Date' },
    { key: 'status', label: 'Status' },
    { key: 'wellName', label: 'Well Name', hrefKey: 'wellNameHref' },
    { key: 'afeType', label: 'AFE Type' },
    { key: 'basin', label: 'Basin' },
    { key: 'county', label: 'County' },
    { key: 'state', label: 'State' },
    { key: 'formation', label: 'Formation' },
  ],
};

const dor = (row: PickerRow) => row as unknown as DivisionOrderResult;

const divisionOrderConfig: RecordKindConfig = {
  href: '/queries/division_orders',
  targetType: 'Salesforce::DivisionOrder',
  searchPlaceholder: 'Search Division Order records…',
  getRowId: (row) => String(dor(row).id),
  columns: [
    column('name', 'Division Order', (row) =>
      linkCell(dash(dor(row).name), dor(row).href)
    ),
    column('source', 'DO Input Source', (row) =>
      linkCell(
        dash(dor(row).divisionOrderSource ?? dor(row).source),
        dor(row).source === 'Provisioned' ? dor(row).sourceDocument?.href : null
      )
    ),
    column('properties', 'Properties', (row) =>
      dash(distinctPropertyCount(dor(row).lineItems))
    ),
    column('dateReceived', 'Date Received', (row) =>
      dash(dor(row).dateReceived)
    ),
    column('operator', 'Operator', (row) => {
      const op = dor(row).operator;
      return linkCell(dash(op?.name), op?.entityHref ?? op?.salesforceHref);
    }),
    column('owner', 'Owner', (row) => {
      // account.name is the operator name on Provisioned DOs, so leave it as text;
      // only the line item's own entity is a safe owner link.
      const accountName = dor(row).account?.name;
      if (accountName) return dash(accountName);
      const line = dor(row).lineItems?.[0];
      return linkCell(dash(line?.entityName), entityPath(line?.entityId));
    }),
    column('createdBy', 'Created By', (row) => dash(dor(row).createdByName)),
    column('createdAt', 'Created At', (row) => dash(dor(row).createdAt)),
    column('updatedAt', 'Updated At', (row) => dash(dor(row).updatedAt)),
  ],
  downloadable: (row) =>
    !!(
      dor(row).sourceDocument?.fileUrl ??
      dor(row).supportingFiles?.[0]?.downloadUrl
    ),
  detailFields: [
    { key: 'doNumber', label: 'DO Number', recordLink: true },
    { key: 'wellName', label: 'Well Name', hrefKey: 'wellNameHref' },
    { key: 'operator', label: 'Operator', hrefKey: 'operatorHref' },
    { key: 'interestType', label: 'Interest Type' },
    { key: 'nriDecimal', label: 'NRI Decimal' },
    { key: 'status', label: 'Status' },
    { key: 'doSource', label: 'DO Source', hrefKey: 'doSourceHref' },
    { key: 'documentType', label: 'Document Type' },
    { key: 'owner', label: 'Owner', hrefKey: 'ownerHref' },
    { key: 'dateReceived', label: 'Date Received' },
    { key: 'dateReturned', label: 'Date Returned' },
    { key: 'dateConfirmed', label: 'Date Confirmed' },
  ],
};

const rc = (row: PickerRow) => row as unknown as RoyaltyCheckResult;

const royaltyCheckConfig: RecordKindConfig = {
  href: '/queries/royalty_checks',
  targetType: 'Salesforce::RoyaltyCheck',
  searchPlaceholder: 'Search Royalty Check records…',
  getRowId: (row) => String(rc(row).id),
  columns: [
    column('name', 'Check Name', (row) =>
      linkCell(dash(rc(row).name), rc(row).href)
    ),
    column('source', 'RC Input Source', (row) =>
      linkCell(
        dash(rcSourceLabel(rc(row).source)),
        rc(row).source === 'Provisioned' ? rc(row).sourceDocument?.href : null
      )
    ),
    column('lineItems', 'Line Items', (row) => dash(rc(row).lineItemCount)),
    column('properties', 'Properties', (row) => dash(rc(row).propertyCount)),
    column('payor', 'Payor', (row) => {
      const p = rc(row).payor;
      return linkCell(dash(p?.name), p?.entityHref ?? p?.salesforceHref);
    }),
    column('payee', 'Payee', (row) => {
      const p = rc(row).payee;
      return linkCell(dash(p?.name), p?.entityHref ?? p?.salesforceHref);
    }),
    column('checkNumber', 'Check Number', (row) => dash(rc(row).checkNumber)),
    column('checkIssueDate', 'Check Issue Date', (row) =>
      dash(rc(row).checkIssueDate)
    ),
    column('checkAmount', 'Check Amount', (row) =>
      dash(fmtUsd(rc(row).checkAmount))
    ),
    column('createdAt', 'Created At', (row) => dash(rc(row).createdAt)),
    column('updatedAt', 'Updated At', (row) => dash(rc(row).updatedAt)),
  ],
  // Royalty Check query rows carry no blob URL, but provisioned / auto-connected
  // checks resolve a source file live server-side, so hint downloadable for them.
  downloadable: (row) => rc(row).source === 'Provisioned',
  detailFields: [
    { key: 'checkName', label: 'Check Name', recordLink: true },
    { key: 'checkNumber', label: 'Check Number' },
    {
      key: 'rcInputSource',
      label: 'RC Input Source',
      hrefKey: 'rcInputSourceHref',
    },
    { key: 'checkIssueDate', label: 'Check Issue Date' },
    { key: 'checkAmountStated', label: 'Check Amount — stated on document' },
    { key: 'checkAmountCalculated', label: 'Check Amount Calculated' },
    { key: 'lineItemVariance', label: 'Line Item Variance' },
    {
      key: 'operatorName',
      label: 'Operator Name',
      hrefKey: 'operatorNameHref',
    },
    { key: 'ownerName', label: 'Owner Name', hrefKey: 'ownerNameHref' },
    { key: 'ownerNumber', label: 'Owner Number' },
    { key: 'lineItems', label: 'Line Items' },
    { key: 'properties', label: 'Properties' },
  ],
};

const wel = (row: PickerRow) => row as unknown as WellsResponse;

const wellConfig: RecordKindConfig = {
  href: '/queries/wells',
  targetType: 'Well',
  searchPlaceholder: 'Search wells…',
  getRowId: (row) => String(wel(row).id),
  columns: [
    column('uwi', 'UWI', (row) => linkCell(dash(wel(row).uwi), wel(row).href)),
    column('wellName', 'Well Name', (row) =>
      dash(wel(row).name ?? wel(row).leaseName)
    ),
    column('operator', 'Operator', (row) => dash(wel(row).currentOperator)),
    column('holeDirection', 'Hole Direction', (row) =>
      dash(wel(row).holeDirection)
    ),
    column('state', 'State', (row) => dash(wel(row).stateAbbreviation)),
    // Sourced from canonicalLandingInterval, not the retired srpLandingZone field —
    // wells.srp_landing_zone no longer exists on the backend.
    column('simLandingZone', 'SIM Landing Zone', (row) =>
      dash(wel(row).canonicalLandingInterval ?? wel(row).vendorLandingZone)
    ),
  ],
  detailFields: [
    { key: 'uwi', label: 'UWI', recordLink: true },
    { key: 'wellName', label: 'Well Name' },
    { key: 'operator', label: 'Operator' },
    { key: 'state', label: 'State' },
    { key: 'simLandingZone', label: 'SIM Landing Zone' },
    { key: 'measuredDepthFt', label: 'Measured Depth (ft)' },
    { key: 'trueVerticalDepthFt', label: 'True Vertical Depth (ft)' },
    { key: 'permitDate', label: 'Permit Date' },
    { key: 'spudDate', label: 'Spud Date' },
    { key: 'completionDate', label: 'Completion Date' },
    { key: 'onProduction', label: 'On Production' },
    { key: 'rigReleaseDate', label: 'Rig Release Date' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'updatedAt', label: 'Updated At' },
  ],
};

// Courthouse and Regulatory rows come from the shared documents search (filtered by
// subtype). Newer fields (filing_number, api_number, filing_date) are read defensively.
interface DocumentPickerRow extends DocumentResponse {
  filingNumber?: string | null;
  apiNumber?: string | null;
  filingDate?: string | null;
}

const doc = (row: PickerRow) => row as unknown as DocumentPickerRow;

const courthouseConfig: RecordKindConfig = {
  href: '/queries/documents',
  targetType: 'Document',
  searchPlaceholder: 'Search Courthouse Documents…',
  extraParams: { documentType: 'courthouse' },
  getRowId: (row) => String(doc(row).id),
  columns: [
    column('documentType', 'Document Type', (row) =>
      linkCell(dash(doc(row).documentType), doc(row).href)
    ),
    column('instrumentNumber', 'Instrument Number', (row) =>
      linkCell(dash(doc(row).instrumentNumber), doc(row).href)
    ),
    column('county', 'County', (row) => dash(doc(row).county?.name)),
    column('state', 'State', (row) =>
      dash(doc(row).county?.state?.abbreviation)
    ),
    column('conveyor', 'Conveyor', (row) =>
      dash(partyNames(doc(row).grantors))
    ),
    column('conveyee', 'Conveyee', (row) =>
      dash(partyNames(doc(row).grantees))
    ),
  ],
  downloadable: (row) => !!str(doc(row).resources?.image?.href),
  detailFields: [
    { key: 'documentType', label: 'Document Type' },
    { key: 'instrumentNumber', label: 'Instrument Number', recordLink: true },
    { key: 'county', label: 'County' },
    { key: 'state', label: 'State' },
    { key: 'volume', label: 'Volume' },
    { key: 'page', label: 'Page' },
    { key: 'effectiveDate', label: 'Effective Date' },
    { key: 'conveyor', label: 'Conveyor' },
    { key: 'conveyee', label: 'Conveyee' },
    { key: 'recordingDate', label: 'Recording Date' },
    { key: 'fileDate', label: 'File Date' },
    { key: 'instrumentDate', label: 'Instrument Date' },
  ],
};

const regulatoryConfig: RecordKindConfig = {
  href: '/queries/documents',
  targetType: 'Document',
  searchPlaceholder: 'Search Regulatory Documents…',
  extraParams: { documentType: 'regulatory' },
  getRowId: (row) => String(doc(row).id),
  columns: [
    column('documentType', 'Document Type', (row) =>
      linkCell(dash(doc(row).documentType), doc(row).href)
    ),
    column('filingNumber', 'Filing Number', (row) =>
      linkCell(dash(doc(row).filingNumber), doc(row).href)
    ),
    column('regulatoryBody', 'Regulatory Body', (row) =>
      dash(doc(row).regulatoryBody?.name)
    ),
    column('state', 'State', (row) =>
      dash(
        doc(row).regulatoryBody?.state?.abbreviation ??
          doc(row).county?.state?.abbreviation
      )
    ),
  ],
  downloadable: (row) => !!str(doc(row).resources?.image?.href),
  detailFields: [
    { key: 'documentType', label: 'Document Type' },
    { key: 'filingNumber', label: 'Filing Number', recordLink: true },
    { key: 'regulatoryBody', label: 'Regulatory Body' },
    { key: 'state', label: 'State' },
    { key: 'county', label: 'County' },
    { key: 'filingDate', label: 'Filing Date' },
    { key: 'apiNumber', label: 'API Number' },
  ],
};

// Per-kind config map for the six record-link kinds.
export const RECORD_KIND_CONFIGS: Partial<
  Record<RelatedItemKind, RecordKindConfig>
> = {
  afe: afeConfig,
  division_order: divisionOrderConfig,
  royalty_check: royaltyCheckConfig,
  courthouse_document: courthouseConfig,
  regulatory_document: regulatoryConfig,
  well: wellConfig,
};

export const recordConfig = (
  kind: RelatedItemKind | null
): RecordKindConfig | null => (kind && RECORD_KIND_CONFIGS[kind]) || null;

// The accordion's field list for a kind: what to label each snapshot key, and
// in what order. The values themselves come from the backend snapshot.
export const recordReaderFields = (kind: RelatedItemKind): ReaderField[] =>
  recordConfig(kind)?.detailFields ?? [];
